import { useState } from "react";
import { Printer, Copy, Check } from "lucide-react";
import { Button } from "../../ui/button";
import { useSelector } from "react-redux";
import {
  getFeedbackForm,
  getProfileForm,
  getWhiteLabelLogos,
} from "../../../redux/reducer/incbotFeedbackReducer";
import logo from "../../../assets/images/incruiter-main-logo.svg";
import { formatDate, formatDuration, resolveFeedbackStatusChip } from "../../../utils/helper";

const ProfileHeader = () => {
  const profileForm = useSelector(getProfileForm);
  const feedbackForm = useSelector(getFeedbackForm);
  const whiteLabelLogos = useSelector(getWhiteLabelLogos);
  const statusChip = resolveFeedbackStatusChip(profileForm?.status, profileForm?.feedbackStatus);
  const [copied, setCopied] = useState(false);

  const handleCopyLink = () => {
    navigator.clipboard
      .writeText(window.location.href)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1000);
      })
      .catch((err) => console.error("Failed to copy link:", err));
  };

  return (
    <div className="bg-white border-none border border-gray-200 p-3 md:p-6">
      <div className="flex flex-col gap-2 w-full align-center justify-center">
        <div className="sm:hidden flex-shrink-0 w-full flex justify-center mb-2">
          <img
            src={whiteLabelLogos?.logo ? whiteLabelLogos?.logo : logo}
            alt="InCruiter"
            className="h-10 w-20 object-contain md:h-12"
          />
        </div>
        <div className="flex gap-2 sm:hidden">
          <div className="flex flex-col gap-2 items-center justify-center">
            <div className="size-28 md:size-32 flex-shrink-0 bg-gradient-to-br from-teal-500 to-blue-600 rounded-2xl flex items-center justify-center text-white text-2xl font-bold overflow-hidden">
              {profileForm?.candidateImage ? (
                <img
                  src={profileForm?.candidateImage}
                  className="w-full h-full object-cover"
                  alt=""
                />
              ) : (
                profileForm?.name
                  ?.split(" ")
                  .map((n: string) => n[0])
                  .join("") || ""
              )}
            </div>
          </div>
          <div className="flex-1 overflow-hidden">
            <h1 className="text-sm md:text-2xl font-bold text-gray-900">
              {profileForm?.name || ""}
            </h1>
            <span className="text-xs md:text-lg text-gray-500">{profileForm?.code || ""}</span>
            <p className="text-gray-700 text-[9px] md:text-md font-medium">
              {profileForm?.position || ""} • {profileForm?.company || "InCruiter"}
            </p>
            <p className="text-gray-500 text-[9px] md:text-sm">
              {profileForm?.email || ""} • {profileForm?.phone?.replace(/\+\+/g, "+") || ""}
            </p>
            <div className="flex items-center gap-2 max-sm:mb-2 flex-wrap">
              <span className="text-sm max-sm:text-[9px]">
                {formatDate(profileForm?.interviewDate, 0)}
              </span>
              <span className="text-sm max-sm:text-[9px]">
                {formatDuration(feedbackForm?.totalDuration || 0)}
              </span>
              {profileForm?.interviewLanguage && (
                <span className="text-sm max-sm:text-[9px] text-gray-600">
                  Interview Language: {profileForm.interviewLanguage}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center max-sm:hidden">
          <div className="size-24 md:size-32 flex-shrink-0 bg-gradient-to-br from-teal-500 to-blue-600 rounded-2xl flex items-center justify-center text-white text-2xl font-bold overflow-hidden">
            {profileForm?.candidateImage ? (
              <img
                src={profileForm?.candidateImage}
                className="w-full h-full object-cover"
                alt=""
              />
            ) : (
              profileForm?.name
                ?.split(" ")
                .map((n: string) => n[0])
                .join("") || ""
            )}
          </div>
          <div className="min-w-0 sm:block max-sm:hidden flex-1 ml-4 overflow-hidden">
            <div className="flex items-center space-x-2 mb-2 flex-wrap">
              <h1 className="text-xl md:text-2xl font-bold text-gray-900 truncate">
                {profileForm?.name || ""}
              </h1>
              <span className="text-md md:text-lg text-gray-500 truncate">
                {profileForm?.code || ""}
              </span>
            </div>
            <p className="text-gray-700 text-sm md:text-md font-medium truncate">
              {profileForm?.position || ""} • {profileForm?.company || "InCruiter"}
            </p>
            <p className="text-gray-500 text-xs md:text-sm truncate">
              {profileForm?.email || ""} • {profileForm?.phone?.replace(/\+\+/g, "+") || ""}
            </p>
            <div className="flex items-center gap-2 max-sm:mb-2 flex-wrap">
              <div className="flex items-center space-x-2 text-gray-600">
                <span className="text-sm max-sm:text-[9px]">
                  {formatDate(profileForm?.interviewDate, 0)}
                </span>
              </div>
              <div className="flex items-center space-x-2 text-gray-600">
                <span className="text-sm max-sm:text-[9px]">
                  {formatDuration(feedbackForm?.totalDuration || 0)}
                </span>
              </div>
              {profileForm?.interviewLanguage && (
                <div className="flex items-center space-x-2 text-gray-600">
                  <span className="text-sm max-sm:text-[9px]">
                    Interview Language: {profileForm.interviewLanguage}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="max-sm:hidden flex-shrink-0">
            <img
              src={whiteLabelLogos?.logo ? whiteLabelLogos?.logo : logo}
              alt="InCruiter"
              className="h-14 w-32 object-contain"
            />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mt-2 md:mt-6 max-sm:flex-col">
        <div className="flex items-center space-x-3 max-sm:w-full max-sm:mb-2">
          {profileForm?.status && (
            <div
              className={`flex items-center space-x-2 px-3 py-1 rounded-full ${
                profileForm.status === "Recommended"
                  ? "bg-green-50 text-green-700"
                  : profileForm.status === "Cautiously Recommended"
                    ? "bg-yellow-50 text-yellow-700"
                    : "bg-red-50 text-red-700"
              }`}
              style={statusChip.style}
            >
              <span className="text-sm font-medium">{statusChip.label}</span>
            </div>
          )}
        </div>

        <div className="flex items-center space-x-2 max-sm:w-full">
          <Button
            onClick={() => window.print()}
            variant="outline"
            size="sm"
            className="pr-11 print:hidden max-sm:w-1/2"
          >
            <Printer className="w-4 h-4" />
            Print
          </Button>
          <Button
            onClick={handleCopyLink}
            variant="outline"
            size="sm"
            className="print:hidden max-sm:w-1/2"
          >
            {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
            {copied ? "Link copied" : "Copy Link"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProfileHeader;
